import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  IconButton,
  Typography,
} from '@mui/material';
import { IconX } from '@tabler/icons-react';
import { AppConfig } from 'AppConfig';
import axios from 'axios';
import PillSelect from 'common/PillSelect';
import SearchField from 'common/SearchField';
import BookmarkIcon from 'icons/BookmarkIcon';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { sortReports } from 'utils/reportSort';
import { hasSmartCardViewPermission, hasPermission } from 'utils/common';
import { ALL_REPORT_TYPE, MANAGE_SAVED_SORT_OPTIONS, REPORT_TYPE_OPTIONS, REPORT_TYPE_ORDER, SORT_OPTIONS_MAP } from './NewHomeConfig';
import { showErrorToast } from 'utils/showErrorToast';
import * as FavoriteReportActions from '../../frontoffice/favoriteReport/actions/action';
import { store } from '../../../store/store';
import CloseIcon from '@mui/icons-material/Close';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import CustomTooltip from 'common/customTooltip';

const colors = {
  border: '#71767A',
  line: '#E0E0E0',
  primary: '#2F54A5',
  heading: '#000000',
  subtitle: '#121A22',
  icon: '#001A47'
};

const MANAGE_SAVED_REPORTS_PREF_KEYS = {
  reportType: 'manageSavedReports.reportType',
  sortBy: 'manageSavedReports.sortBy'
};

const getStoredPref = (key) => {
  try {
    return sessionStorage.getItem(key) || '';
  } catch (err) {
    return '';
  }
};

const setStoredPref = (key, value) => {
  try {
    if (!value) return;
    sessionStorage.setItem(key, value);
  } catch (err) {
    // no-op when storage is unavailable
  }
};


const emptyStateStyles = {
  borderRadius: '16px',
  backgroundColor: '#F8F8F8',
  width: '100%',
  alignSelf: 'stretch',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  flexGrow: 1,
  flexShrink: 1,
  px: { xs: 2, md: '173px' },
  py: { xs: 4, md: '83px' },
  minHeight: { xs: 360, md: 520 },
  height: 'auto',
  maxHeight: 'none'
};

const discardDialogStyles = {
  paper: {
    width: { xs: 'calc(100% - 32px)', sm: 564 },
    maxWidth: 'none',
    borderRadius: '24px',
    backgroundColor: '#FFFFFF',
    boxShadow: '0px 22px 11px rgba(0, 0, 0, 0.12), 0px 7px 3.5px rgba(0, 0, 0, 0.08)',
    m: 2,
    overflow: 'hidden'
  },
  layout: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: {
      xs: '24px',
      sm: '24px',
    },
    p: {
      xs: '24px 16px',
      sm: '32px',
    },
  },
  contentStack: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '24px',
    width: '100%',
    pb: '0px'
  },
  title: {
    p: 0,
    fontFamily: 'Rubik',
    fontWeight: 600,
    fontSize: 20,
    lineHeight: '150%',
    letterSpacing: '0.2px',
    color: colors.heading,
    whiteSpace: 'nowrap'
  },
  content: {
    p: 0
  },
  message: {
    fontFamily: 'Noto Sans',
    fontWeight: 400,
    fontSize: 16,
    lineHeight: '150%',
    letterSpacing: 0,
    color: colors.heading,
    width: '100%'
  },
  actions: {
    p: 0,
    gap: {
      xs: '16px',
      sm: '16px',
    },
    justifyContent: {
      xs: 'center',
      sm: 'flex-end',
    },
    alignItems: 'center',
    width: '100%',
    flexDirection: {
      xs: 'column',
      sm: 'row',
    },
    '& > :not(:first-of-type)': {
      marginLeft: '0px',
    },
  },
  discardButton: {
    backgroundColor: '#FFFFFF',
    borderRadius: '24px',
    textTransform: 'none',
    fontFamily: 'Noto Sans',
    fontWeight: 600,
    fontSize: 16,
    lineHeight: '150%',
    height: 44,
    minWidth: {
      xs: '100%',
      sm: 'unset',
    },
    px: '16px',
    gap: '0px',
    justifyContent: 'center',
    whiteSpace: 'nowrap',
    color: colors.primary,
    borderColor: colors.primary,
    '&:hover': { borderColor: '#214A99', color: '#214A99' }
  },
  keepEditingButton: {
    borderRadius: '24px',
    textTransform: 'none',
    fontFamily: 'Noto Sans',
    fontWeight: 600,
    fontSize: 16,
    lineHeight: '150%',
    height: 44,
    minWidth: {
      xs: '100%',
      sm: 'unset',
    },
    px: '16px',
    gap: '0px',
    justifyContent: 'center',
    whiteSpace: 'nowrap',
    color: '#FFFFFF',
    backgroundColor: colors.primary,
    '&:hover': { backgroundColor: '#214A99' }
  },
  titleContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    width: '100%',
  },
  closeButton: {
    p: 0,
    width: 24,
    height: 24,
    ml: 2,
    color: colors.heading,
    flexShrink: 0,
    position: 'absolute',
    top: 24,
    right: 16,
  }
};

const toUpper = (value) => (value ? String(value).trim().toUpperCase() : '');
const getItemType = (item) => {
  const raw = toUpper(item?.category || item?.reportType);
  if (raw === 'EXCLUSIVE') return toUpper(AppConfig.REPORT_CATEGORY.CUSTOM.code);
  if (raw.startsWith('ADHOC') || raw === 'AD_HOC') return toUpper(AppConfig.REPORT_CATEGORY.AD_HOC.code);
  return raw;
};
const isExclusiveReport = (item) => toUpper(item?.category || item?.reportType) === 'EXCLUSIVE';
const isAdHocReport = (item) => getItemType(item) === toUpper(AppConfig.REPORT_CATEGORY.AD_HOC.code);

const getItemKey = (item) => (item?.id != null ? `${getItemType(item)}::${item.id}` : null);

const filterAndSort = ({ reports, type, sortBy, search }) => {
  const list = Array.isArray(reports) ? reports.filter((item) => item?.favorited) : [];
  const term = search.trim().toLowerCase();
  const isAll = toUpper(type) === ALL_REPORT_TYPE;
  const targetKey = toUpper(type);

  const isMatchType = (item) => {
    if (isAll) return true;
    if (!targetKey) return true;
    return getItemType(item) === targetKey;
  };

  const filtered = list.filter((item) => {
    const matchesType = isMatchType(item);
    if (!matchesType) return false;
    if (!term) return true;
    const text = `${item?.displayText || ''}`.toLowerCase();
    return text.includes(term);
  });

  const isSmart = targetKey === 'SMART';
  const sorted = sortReports(filtered, sortBy, { smart: isSmart });

  // Keep global chronological order for Recently Saved; only apply type grouping to other non-alpha sorts.
  if (isAll && sortBy !== 'alphabetical_asc' && sortBy !== 'recently_saved') {
    sorted.sort((a, b) => {
      const aTypeIdx = REPORT_TYPE_ORDER.indexOf(getItemType(a));
      const bTypeIdx = REPORT_TYPE_ORDER.indexOf(getItemType(b));
      const aOrder = aTypeIdx === -1 ? REPORT_TYPE_ORDER.length : aTypeIdx;
      const bOrder = bTypeIdx === -1 ? REPORT_TYPE_ORDER.length : bTypeIdx;
      return aOrder - bOrder;
    });
  }

  return sorted;
};


const normalize = (str) => (str || '').replace(/[\s_-]/g, '').toLowerCase();

const getDefaultSortByForType = (type) => {
  const options = SORT_OPTIONS_MAP[type] || MANAGE_SAVED_SORT_OPTIONS;
  return options?.[0]?.value || 'alphabetical_asc';
};

const getNotificationText = (item, alertKeys, subscriptionViewUrls) => {
  if (!item?.displayText) return 'You do not get notifications for this report.';
  const normalizedDisplayText = normalize(item.displayText);
  const normalizedReptSource = normalize(item.reptSource || '');

  const hasAlert = alertKeys.has(normalizedDisplayText);
  const hasSubscription = subscriptionViewUrls.has(normalizedReptSource);

  if (hasSubscription && hasAlert) {
    return [
      'You get scheduled email snapshots of this report.',
      'You get notified when data reaches a set threshold.'
    ];
  }
  if (hasSubscription) return 'You get scheduled email snapshots of this report.';
  if (hasAlert) return 'You get notified when data reaches a set threshold.';
  return 'You do not get notifications for this report.';
};

const ManageSavedReportsDrawer = ({ open, onClose, reports, onUnsaveReports }) => {
  const user = useSelector((state) => state.user);
  const permissions = useSelector((state) => state.user.permissions);
  const canViewSmartCards = hasSmartCardViewPermission(user);
  const canViewAdHoc = hasPermission(AppConfig.PERMISSION.FO_AD_HOC, permissions);
  const canViewExclusive = hasPermission(AppConfig.PERMISSION.FO_EXCLUSIVE_REPORTS, permissions);
  const accessibleReports = useMemo(
    () => (Array.isArray(reports) ? reports.filter((item) => (
      (canViewExclusive || !isExclusiveReport(item)) &&
      (canViewAdHoc || !isAdHocReport(item))
    )) : []),
    [canViewAdHoc, canViewExclusive, reports]
  );
  const [reportType, setReportType] = useState(ALL_REPORT_TYPE);
  const [sortBy, setSortBy] = useState(getDefaultSortByForType(ALL_REPORT_TYPE));
  const [searchTerm, setSearchTerm] = useState('');
  const [isUnsaveLoading, setIsUnsaveLoading] = useState(false);
  const [isDiscardDialogOpen, setIsDiscardDialogOpen] = useState(false);
  const [pendingUnsaveKeys, setPendingUnsaveKeys] = useState([]);
  const [alertWorkbooks, setAlertWorkbooks] = useState(new Set());
  const [subscriptionWorkbooks, setSubscriptionWorkbooks] = useState(new Set());
  const prevOpenRef = useRef(false);
  const reportsRef = useRef(accessibleReports);
  reportsRef.current = accessibleReports;

  const typeOptions = useMemo(() => REPORT_TYPE_OPTIONS.filter((option) => {
    if (!option?.isDivider && option?.value === 'saved') return false;
    if (!canViewSmartCards && option?.value === AppConfig.REPORT_CATEGORY.SMART.code) return false;
    if (!canViewAdHoc && option?.value === AppConfig.REPORT_CATEGORY.AD_HOC.code) return false;
    return true;
  }), [canViewSmartCards, canViewAdHoc]);
  const sortOptions = useMemo(() => SORT_OPTIONS_MAP[reportType] || MANAGE_SAVED_SORT_OPTIONS, [reportType]);
  const totalFavorited = useMemo(() => (Array.isArray(accessibleReports) ? accessibleReports.filter((item) => item?.favorited).length : 0), [accessibleReports]);
  const showInitialEmptyState = totalFavorited === 0;

  const pendingUnsaveKeySet = useMemo(() => new Set(pendingUnsaveKeys), [pendingUnsaveKeys]);
  const hasUnsavedChanges = pendingUnsaveKeys.length > 0;

  const filtered = useMemo(
    () => filterAndSort({ reports: accessibleReports, type: reportType, sortBy, search: searchTerm }),
    [accessibleReports, reportType, sortBy, searchTerm]
  );

  const visibleKeys = useMemo(
    () => (Array.isArray(filtered) ? filtered.filter((item) => item?.id != null).map((item) => getItemKey(item)) : []),
    [filtered]
  );

  const visibleKeySet = useMemo(() => new Set(visibleKeys), [visibleKeys]);

  const allMarkedUnsaved = useMemo(() => {
    if (visibleKeys.length === 0) return false;
    return visibleKeys.every((key) => pendingUnsaveKeySet.has(key));
  }, [visibleKeys, pendingUnsaveKeySet]);

  const handleReportTypeChange = useCallback((nextType) => {
    const nextSort = getDefaultSortByForType(nextType);
    setReportType(nextType);
    setSortBy(nextSort);
  }, []);

  const handleUnsaveSingle = useCallback(
    (item) => {
      const key = getItemKey(item);
      if (key == null) return;
      setPendingUnsaveKeys((prev) =>
        prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
      );
    },
    []
  );

  const handleMarkAllUnsaved = useCallback(() => {
    setPendingUnsaveKeys((prev) => {
      if (visibleKeys.length === 0) return prev;
      const next = new Set(prev);
      visibleKeys.forEach((key) => next.add(key));
      return Array.from(next);
    });
  }, [visibleKeys]);

  const handleMarkAllSaved = useCallback(() => {
    setPendingUnsaveKeys((prev) => {
      if (visibleKeys.length === 0) return prev;
      return prev.filter((key) => !visibleKeySet.has(key));
    });
  }, [visibleKeySet, visibleKeys.length]);

  const closeDrawer = useCallback(() => {
    if (onClose) {
      onClose();
    }
  }, [onClose]);

  const requestClose = useCallback(() => {
    if (isUnsaveLoading) return;
    if (hasUnsavedChanges) {
      setIsDiscardDialogOpen(true);
      return;
    }
    closeDrawer();
  }, [closeDrawer, hasUnsavedChanges, isUnsaveLoading]);

  const handleDiscardChanges = useCallback(() => {
    setIsDiscardDialogOpen(false);
    setPendingUnsaveKeys([]);
    closeDrawer();
  }, [closeDrawer]);

  const handleKeepEditing = useCallback(() => {
    setIsDiscardDialogOpen(false);
  }, []);

  const handleSaveChanges = useCallback(async () => {
    if (pendingUnsaveKeys.length > 0 && onUnsaveReports) {
      const itemsToUnsave = (Array.isArray(reports) ? reports : []).filter((item) => pendingUnsaveKeys.includes(getItemKey(item)));
      if (itemsToUnsave.length > 0) {
        setIsUnsaveLoading(true);
        try {
          await onUnsaveReports(itemsToUnsave);
          await store.dispatch(FavoriteReportActions.getFavoriteReport());
        } catch(err) {
          const statusCode = err?.response?.status ?? err?.status;
          console.error('Error unsaving reports:', statusCode ? `HTTP ${statusCode}` : err);
          showErrorToast("Unsave Failed" , "Unable to un-save report. Please try again.", "455px" , "355px" , "#88041E" , true);
          setPendingUnsaveKeys([]);
          return;
        } finally {
          setIsUnsaveLoading(false);
        }
      }
    }
    setStoredPref(MANAGE_SAVED_REPORTS_PREF_KEYS.reportType, reportType);
    setStoredPref(MANAGE_SAVED_REPORTS_PREF_KEYS.sortBy, sortBy);
    setPendingUnsaveKeys([]);
    closeDrawer();
  }, [accessibleReports, closeDrawer, pendingUnsaveKeys, onUnsaveReports, reportType, sortBy]);

  useEffect(() => {
    const justOpened = open && !prevOpenRef.current;
    prevOpenRef.current = open;

    if (!open || !justOpened) return;

    const availableTypeValues = new Set(
      typeOptions
        .filter((option) => !option?.isDivider)
        .map((option) => option.value)
    );
    const storedType = getStoredPref(MANAGE_SAVED_REPORTS_PREF_KEYS.reportType);
    const nextType = availableTypeValues.has(storedType) ? storedType : ALL_REPORT_TYPE;

    const optionsForType = SORT_OPTIONS_MAP[nextType] || MANAGE_SAVED_SORT_OPTIONS;
    const storedSort = getStoredPref(MANAGE_SAVED_REPORTS_PREF_KEYS.sortBy);
    const nextSort = optionsForType.some((option) => option.value === storedSort)
      ? storedSort
      : getDefaultSortByForType(nextType);

    setReportType(nextType);
    setSortBy(nextSort);
    setSearchTerm('');
    setPendingUnsaveKeys([]);
    setIsDiscardDialogOpen(false);

    const fetchNotificationData = async () => {
      try {
        // Collect both displayText (for data alert matching) and reptSource (for subscription matching)
        const savedReports = (Array.isArray(reportsRef.current) ? reportsRef.current : []).filter((r) => r?.favorited);
        const reportNames = savedReports.map((r) => r?.displayText).filter(Boolean);
        const reportSources = savedReports.map((r) => r?.reptSource).filter(Boolean);

        if (reportNames.length === 0 && reportSources.length === 0) {
          setAlertWorkbooks(new Set());
          setSubscriptionWorkbooks(new Set());
          return;
        }

        const res = await axios.post(
          `${AppConfig.FO_INITIAL_URL}/tbl/userReportNotifications`,
          { reportNames, reportSources }
        ).catch(() => null);

        const alertWbs = new Set();
        const alerts = res?.data?.dataAlerts;
        if (Array.isArray(alerts)) {
          alerts.forEach((a) => {
            const wbName = a?.view?.workbook?.name;
            if (wbName) {
              alertWbs.add(normalize(wbName));
            }
          });
        }
        setAlertWorkbooks(alertWbs);

        const subUrls = new Set();
        const subs = res?.data?.subscriptionViewUrls;
        if (Array.isArray(subs)) {
          subs.forEach((url) => {
            if (url) subUrls.add(normalize(url));
          });
        }
        setSubscriptionWorkbooks(subUrls);
      } catch (err) {
        console.error('Error fetching notification data:', err);
      }
    };
    fetchNotificationData();
  }, [open, typeOptions]);

  const renderTypeLabel = (value) => typeOptions.find((o) => !o.isDivider && o.value === value)?.label || value || '';
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={requestClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', md: 672 },
          maxWidth: '100%',
          boxShadow: '-12px 0px 28px rgba(17, 17, 17, 0.08)',
          borderRadius: 0,
          backgroundColor: '#FFFFFF'
        }
      }}
    >
      <Box display="flex" flexDirection="column" height="100vh" bgcolor="#FFFFFF">
        <Box px={3} py={3} borderBottom={`1px solid ${colors.border}`}>
          <Box display="flex" alignItems="center" justifyContent="flex-end">
            <IconButton
              aria-label="Close manage saved reports"
              onClick={requestClose}
              edge="end"
              sx={{
                color: '#1F1F1F',
                '&:hover svg': { color: '#000000' }
              }}
            >
              <IconX size={24} color='#000' />
            </IconButton>
          </Box>
        </Box>

        <Box
          flex={1}
          display="flex"
          flexDirection="column"
          overflow={showInitialEmptyState ? 'hidden' : 'auto'}
          px={3}
          pt={3}
          pb={5}
        >
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={3} gap={2}>
            <Typography
              sx={{
                fontFamily: 'Rubik',
                fontWeight: 600,
                fontSize: 20,
                lineHeight: '30px',
                color: colors.heading
              }}
            >
              Manage Saved Reports
            </Typography>
          </Box>

          {showInitialEmptyState ? (
            <Box sx={emptyStateStyles}>
              <Typography
                sx={{
                  fontFamily: 'Noto Sans',
                  fontWeight: 600,
                  fontSize: 16,
                  lineHeight: '24px',
                  color: colors.heading
                }}
              >
                No saved reports.
              </Typography>
            </Box>
          ) : (
            <>
              <Box display="grid" gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr' }} gap={3} mb={3}>
                <Box>
                  <Typography sx={{ fontFamily: 'Noto Sans', fontWeight: 600, fontSize: 14, color: colors.heading, mb: 0.5 }}>
                    Report Type
                  </Typography>
                  <PillSelect
                    value={reportType}
                    onChange={(e) => handleReportTypeChange(e.target.value)}
                    options={typeOptions}
                    sx={{ height: '48px' }}
                  />
                </Box>
                <Box>
                  <Typography sx={{ fontFamily: 'Noto Sans', fontWeight: 600, fontSize: 14, color: colors.heading, mb: 0.5 }}>
                    Sort By
                  </Typography>
                  <PillSelect
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    options={sortOptions}
                    sx={{ height: '48px' }}
                  />
                </Box>
              </Box>

              <SearchField
                key={reportType}
                placeholder={`Search ${reportType === ALL_REPORT_TYPE ? 'All' : renderTypeLabel(reportType)} Reports`}
                width="100%"
                height="48px"
                mb={0}
                searchTerm={searchTerm}
                searchReports={{
                  setSearchTerm
                }}
              />

              {!showInitialEmptyState && (
                <Box display="flex" justifyContent="flex-end" sx={{ mt: 3 }}>
                  <Button
                    variant="outlined"
                    onClick={allMarkedUnsaved ? handleMarkAllSaved : handleMarkAllUnsaved}
                    disabled={isUnsaveLoading || visibleKeys.length === 0}
                    sx={{
                      borderRadius: '24px',
                      textTransform: 'none',
                      fontFamily: 'Noto Sans',
                      fontWeight: 600,
                      fontSize: 12,
                      height: 28,
                      px: '10px',
                      color: colors.primary,
                      borderColor: colors.primary,
                      '&:hover': {
                        backgroundColor: '#D8E6FF',
                        borderColor: '#001A47',
                        color: '#001A47',
                      },
                    }}
                  >
                    {allMarkedUnsaved ? 'Mark All as Saved' : 'Mark All as Unsaved'}
                  </Button>
                </Box>
              )}
            </>
          )}

          {!showInitialEmptyState && (filtered.length === 0 ? (
            <Box
              sx={{
                ...emptyStateStyles,
                mt: 2,
                border: `1px solid ${colors.line}`,
                minHeight: { xs: 240, md: 360 },
                height: 'auto',
                maxHeight: 'none',
                px: { xs: 2, md: 6 },
                py: { xs: 4, md: 6 },
                flexGrow: 0
              }}
            >
              <Typography sx={{ fontFamily: 'Noto Sans', color: colors.subtitle, fontSize: 14 }}>
                {searchTerm ? 'No results found' : 'No saved reports.'}
              </Typography>
            </Box>
          ) : (
            filtered.map((item, idx) => {
              const description = getNotificationText(item, alertWorkbooks, subscriptionWorkbooks);
              const isLast = idx === filtered.length - 1;
              const itemKey = getItemKey(item);
              const isMarkedUnsaved = pendingUnsaveKeySet.has(itemKey);
              return (
                <Box
                  key={item?.id || `${item?.displayText}-${idx}`}
                  display="flex"
                  alignItems="stretch"
                  gap={0}
                  borderBottom={isLast ? 'none' : `1px solid ${colors.border}`}
                >
                  <Box flex={1} pt={2.5} pb={3} pr={3} display="flex" flexDirection="column">
                    <Box display="flex" alignItems="center" justifyContent="space-between" gap={3}>
                      <Typography
                        sx={{
                          fontFamily: 'Noto Sans',
                          fontWeight: 600,
                          fontSize: 14,
                          color: colors.heading
                        }}
                      >
                        {item?.displayText || 'Report Title'}
                      </Typography>

                      <Box display="flex" alignItems="center" justifyContent="flex-end" gap={2}>
                        <CustomTooltip
                          title={isMarkedUnsaved ? 'Save' : 'Unsave'}
                          arrow
                          padding="8px 12px"
                          borderRadius="8px 0px 8px 8px"
                          width={!isMarkedUnsaved ? '81px' : '63px'}
                          height="42px"
                          popperProps={{
                            modifiers: [
                              {
                                name: 'offset',
                                options: {
                                  offset: [-9, -11]
                                }
                              }
                            ]
                          }}>
                          <span style={{ display: 'inline-flex' }}>
                            <IconButton
                              aria-label={isMarkedUnsaved ? 'Save report' : 'Unsave report'}
                              onClick={() => handleUnsaveSingle(item)}
                              disabled={isUnsaveLoading}
                              size="small"
                              disableRipple
                              disableFocusRipple
                              disableTouchRipple
                              sx={{
                                p: 0,
                                '&:hover': { backgroundColor: 'transparent' },
                                '&.Mui-focusVisible': { backgroundColor: 'transparent' }
                              }}
                            >
                              <BookmarkIcon filled={!isMarkedUnsaved} size={20} color={colors.icon} />
                            </IconButton>
                          </span>
                        </CustomTooltip>
                      </Box>
                    </Box>

                    <Typography sx={{ mt: 2, fontFamily: 'Noto Sans', fontSize: 12, color: colors.subtitle }}>
                      {Array.isArray(description)
                        ? description.map((line, i) => (
                            <React.Fragment key={i}>
                              {line}
                              {i < description.length - 1 && <br />}
                            </React.Fragment>
                          ))
                        : description}
                    </Typography>
                  </Box>
                </Box>
              );
            })
          ))}
        </Box>

        {!showInitialEmptyState && (
          <Box borderTop={`1px solid ${colors.border}`} px={2} py={3} bgcolor="#FFFFFF">
            <Box display="flex" justifyContent="flex-end" gap={2}>
              <Button
                variant="outlined"
                onClick={requestClose}
                sx={{
                  borderRadius: '24px',
                  textTransform: 'none',
                  fontFamily: 'Noto Sans',
                  fontWeight: 600,
                  fontSize: 16,
                  height: 44,
                  px: 2,
                  color: colors.primary,
                  borderColor: colors.primary,
                  '&:hover': {
                    backgroundColor: '#D8E6FF',
                    borderColor: '#001A47',
                    color: '#001A47',
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSaveChanges}
                disabled={isUnsaveLoading}
                sx={{
                  borderRadius: '24px',
                  textTransform: 'none',
                  fontFamily: 'Noto Sans',
                  fontWeight: 600,
                  fontSize: 16,
                  height: 44,
                  px: 2,
                  backgroundColor: colors.primary,
                  '&:hover': { backgroundColor: '#214A99' }
                }}
              >
                Save Changes
              </Button>
            </Box>
          </Box>
        )}
      </Box>

      <Dialog
        open={isDiscardDialogOpen}
        onClose={handleKeepEditing}
        PaperProps={{ sx: discardDialogStyles.paper }}
        BackdropProps={{
          sx: {
            backgroundColor: 'rgba(17, 17, 17, 0.4)'
          }
        }}
        aria-labelledby="discard-saved-reports-title"
        aria-describedby="discard-saved-reports-description"
      >
        <Box sx={discardDialogStyles.layout}>
          <Box sx={discardDialogStyles.contentStack}>
            <DialogTitle id="discard-saved-reports-title" sx={discardDialogStyles.title}>
              <Box sx={discardDialogStyles.titleContainer}>
                <Typography variant="inherit">
                  Discard Changes?
                </Typography>
                {isMobile && (
                  <IconButton size="small" onClick={handleKeepEditing} sx={discardDialogStyles.closeButton}>
                      <CloseIcon fontSize="small"/>
                  </IconButton>
                )}
              </Box>
            </DialogTitle>
            <DialogContent sx={discardDialogStyles.content}>
              <Typography id="discard-saved-reports-description" sx={discardDialogStyles.message}>
                You made changes to your saved reports. If you leave now, your changes will not be saved.
              </Typography>
            </DialogContent>
          </Box>
          <DialogActions sx={discardDialogStyles.actions}>
            <Button variant="outlined" onClick={handleDiscardChanges} sx={discardDialogStyles.discardButton}>
              Discard Changes
            </Button>
            <Button variant="contained" onClick={handleKeepEditing} sx={discardDialogStyles.keepEditingButton}>
              Keep Editing
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
    </Drawer>
  );
};

export default ManageSavedReportsDrawer;
